import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, Star } from "lucide-react";
import { useStore } from "../../lib/store";
import { money } from "../../lib/api";

export const Badge = ({ label }) =>
  label ? (
    <span className={`absolute left-3 top-3 z-10 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-white ${label === "Sale" ? "bg-tangerine-deep" : "bg-plum"}`}>
      {label}
    </span>
  ) : null;

export const ProductCard = ({ product, index = 0 }) => {
  const { addToCart, toggleWish, isWished } = useStore();
  const wished = isWished(product.id);
  const tid = `product-card-${product.id}`;
  return (
    <motion.article
      data-testid={tid}
      className="group relative flex flex-col"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.1 }}
      transition={{ duration: 0.7, delay: (index % 4) * 0.08, ease: [0.22, 1, 0.36, 1] }}
    >
      <div className="relative overflow-hidden rounded-[1.75rem] rounded-tl-[4rem] bg-cream-warm aspect-[4/5]">
        {product.stock === 0 ? (
          <span className="absolute left-3 top-3 z-10 rounded-full bg-plum px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-cream">Sold out</span>
        ) : <Badge label={product.badge} />}
        <button
          data-testid={`${tid}-wishlist`}
          onClick={() => toggleWish(product)}
          aria-label={wished ? "Remove from wishlist" : "Add to wishlist"}
          className="absolute right-3 top-3 z-10 grid h-10 w-10 place-items-center rounded-full bg-white/90 backdrop-blur transition-transform duration-300 hover:scale-110 active:scale-90"
        >
          <Heart className={`h-[18px] w-[18px] transition-colors ${wished ? "fill-magenta text-magenta" : "text-plum"}`} />
        </button>
        <Link to={`/product/${product.id}`} data-testid={`${tid}-link`} className="block h-full">
          <img
            src={product.image}
            alt={product.name}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-[1.2s] ease-[cubic-bezier(.22,1,.36,1)] group-hover:scale-[1.08]"
          />
          <span className="pointer-events-none absolute inset-0 bg-gradient-to-t from-plum/40 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
        </Link>
        <button
          data-testid={`${tid}-add`}
          disabled={product.stock === 0}
          onClick={() => addToCart(product, { size: product.sizes?.[2] ?? product.sizes?.[0] ?? null, color: product.colors?.[0] ?? null })}
          className="absolute inset-x-3 bottom-3 rounded-full bg-magenta py-3 text-[11px] font-bold uppercase tracking-[0.22em] text-white transition-[transform,background-color] duration-500 hover:bg-plum disabled:cursor-not-allowed disabled:bg-plum/60 md:translate-y-[140%] md:group-hover:translate-y-0"
        >
          {product.stock === 0 ? "Sold Out" : "Add to Cart"}
        </button>
      </div>
      <div className="mt-4 flex items-start justify-between gap-3 px-1">
        <div>
          <p className="eyebrow text-[10px] text-plum/60">{product.category}</p>
          <Link to={`/product/${product.id}`} className="mt-1 block font-display text-lg leading-tight text-plum hover:text-magenta transition-colors">
            {product.name}
          </Link>
          <div className="mt-1.5 flex items-center gap-1 text-xs text-plum/70">
            <Star className="h-3.5 w-3.5 fill-gold text-gold" /> {product.rating}
          </div>
          {product.stock != null && product.stock > 0 && product.stock <= 5 && (
            <p data-testid={`${tid}-low-stock`} className="mt-1 text-[11px] font-semibold text-magenta">Only {product.stock} left</p>
          )}
        </div>
        <div className="text-right">
          <p data-testid={`${tid}-price`} className="font-semibold text-plum">{money(product.price)}</p>
          {product.compare_at && <p className="text-xs text-plum/50 line-through">{money(product.compare_at)}</p>}
        </div>
      </div>
    </motion.article>
  );
};
